/**
 * Registry-specific rules for GO cancellation requirements
 * Based on DEV-309 spec requirements for address validation and other country-specific rules
 */

import { ANY_AIB_REGISTRY_SENTINEL } from "@/constants";
import { getYear, isAfter, parseISO, startOfDay } from "date-fns";

export interface RegistryRule {
  requiresAddress: boolean;
  cancellationSupported: boolean;
}

export const ANY_AIB_EFFECTIVE_REGION = "NO";

/** Resolve the "Any AIB" sentinel to its effective registry (Norway); pass every other value through. */
export const resolveEffectiveRegion = (
  registryCountryRegion: string | null | undefined
): string =>
  registryCountryRegion === ANY_AIB_REGISTRY_SENTINEL
    ? ANY_AIB_EFFECTIVE_REGION
    : (registryCountryRegion ?? "");

/**
 * Registry rules mapping based on registry country/region codes from SUPPORTED_REGISTRIES_WITH_LABELS
 * Countries requiring address (beneficiary location): Belgium (Federal), Croatia, Finland, France,
 * Greece, Hungary, Ireland, Italy, Latvia, Lithuania, Denmark, Luxembourg, Norway, Portugal
 * (if beneficiary is a non-registered end-consumer), Serbia, Sweden
 */
export const CANCELLATION_REGISTRY_RULES: Record<string, RegistryRule> = {
  // Countries requiring address
  "BE-FED": { requiresAddress: true, cancellationSupported: false }, // Belgium (Federal) (CREG)
  HR: { requiresAddress: true, cancellationSupported: true }, // Croatia (HROTE)
  FI: { requiresAddress: true, cancellationSupported: true }, // Finland (Finextra)
  FR: { requiresAddress: true, cancellationSupported: true }, // France (EEX) — Grexel platform
  GR: { requiresAddress: true, cancellationSupported: false }, // Greece (DAPEEP) - cancellation disabled until DAPEEP's GO tariff is confirmed (matches REGISTRY_MAPPINGS["GR"])
  HU: { requiresAddress: true, cancellationSupported: true }, // Hungary (MEKH)
  IE: { requiresAddress: true, cancellationSupported: true }, // Ireland (SEMO)
  IT: { requiresAddress: true, cancellationSupported: true }, // Italy (GSE)
  LV: { requiresAddress: true, cancellationSupported: true }, // Latvia (AST)
  LT: { requiresAddress: true, cancellationSupported: true }, // Lithuania (LITGRID)
  LU: { requiresAddress: true, cancellationSupported: true }, // Luxembourg (ILR)
  NO: { requiresAddress: true, cancellationSupported: true }, // Norway (Statnett)
  PT: { requiresAddress: true, cancellationSupported: true }, // Portugal (EEGO) - spec mentions conditional for non-registered end-consumer, but we simplify to always require
  RS: { requiresAddress: true, cancellationSupported: true }, // Serbia (EMS)
  SE: { requiresAddress: true, cancellationSupported: true }, // Sweden (Energy Agency, Cesar) — Grexel platform
  DK: { requiresAddress: true, cancellationSupported: true }, // Denmark (Energinet)

  // Countries not requiring address (existing registries not listed above)
  SI: { requiresAddress: false, cancellationSupported: true }, // Slovenia (Borzen)
  CY: { requiresAddress: false, cancellationSupported: true }, // Cyprus (TSO-Cy)
  DE: { requiresAddress: false, cancellationSupported: false }, // Germany (HKNR)
  ES: { requiresAddress: false, cancellationSupported: true }, // Spain (CNMC)
  AT: { requiresAddress: false, cancellationSupported: true }, // Austria (E-Control)
  "BE-BRU": { requiresAddress: false, cancellationSupported: false }, // Belgium (Brussels) (BRUGEL) - only Federal requires address
  "BE-FLA": { requiresAddress: false, cancellationSupported: true }, // Belgium (Flanders) (VREG) - only Federal requires address
  CZ: { requiresAddress: false, cancellationSupported: true }, // Czech Republic (OTE)
  EE: { requiresAddress: false, cancellationSupported: true }, // Estonia (Elering)
  SK: { requiresAddress: false, cancellationSupported: true }, // Slovakia (OKTE)
  "BE-WAL": { requiresAddress: false, cancellationSupported: true }, // Belgium (Wallonia) (e-CWaPE) - only Federal requires address
  PL: { requiresAddress: false, cancellationSupported: true }, // Poland (TGE)
  CH: { requiresAddress: false, cancellationSupported: true }, // Switzerland (Pronovo)
  NL: { requiresAddress: false, cancellationSupported: true }, // Netherlands (VertiCer)
  IS: { requiresAddress: false, cancellationSupported: true }, // Iceland (Landsnet)
};

/**
 * Global constant for prior-year cancellation cutoff
 * Prior-year consumption cancellations are allowed only until March 31 of the following year
 */
export const PRIOR_YEAR_CUTOFF = { month: 3, day: 31 };

/**
 * Check if a prior-year period is allowed for cancellation based on the global March 31 cutoff.
 * Uses date-only comparison to avoid time-of-day issues (e.g. March 31 at 15:00 > March 31 at 00:00).
 */
export const isPriorYearPeriodAllowed = (
  today: Date,
  periodDate: string
): boolean => {
  const periodYear = getYear(parseISO(periodDate));
  if (Number.isNaN(periodYear)) {
    return false;
  }

  const todayDateOnly = startOfDay(today);

  if (periodYear >= getYear(todayDateOnly)) {
    return true;
  }

  const cutoffDate = new Date(
    periodYear + 1,
    PRIOR_YEAR_CUTOFF.month - 1,
    PRIOR_YEAR_CUTOFF.day
  );
  return !isAfter(todayDateOnly, cutoffDate);
};

/**
 * Get the registry rule for a given registry country/region code
 * @param registryCountryRegion - Registry code from SUPPORTED_REGISTRIES_WITH_LABELS
 * @returns Registry rule or default rule if not found
 */
export const getRegistryRule = (
  registryCountryRegion: string
): RegistryRule => {
  return (
    CANCELLATION_REGISTRY_RULES[
      resolveEffectiveRegion(registryCountryRegion)
    ] ?? {
      requiresAddress: false,
      cancellationSupported: true,
    }
  );
};

/**
 * Registries served by the Grexel platform. Mirrors `GREXEL_PLATFORM_COUNTRIES` in
 * `app/registry/registry_constants.py` — the shared GREXEL integration plus the Cesar (SE) and
 * EEX (FR) deployments, whose registry classes subclass `GrexelRegistry`.
 */
const GREXEL_PLATFORM_REGIONS = new Set([
  "LV",
  "LT",
  "DK",
  "HR",
  "HU",
  "BE-FED",
  "SE",
  "FR",
]);

/**
 * Whether the registry can keep a cancellation statement behind its own authentication instead of
 * publishing an openly accessible PDF URL (DEV-583) — a capability of the Grexel platform, so it
 * holds for exactly the registries in GREXEL_PLATFORM_REGIONS.
 *
 * `resolveEffectiveRegion` maps the "Any AIB" sentinel to Norway (not a Grexel registry) and
 * null/undefined to the empty string, so neither is in the set and both are false.
 */
export const supportsPrivateCancellation = (
  registryCountryRegion: string | null | undefined
): boolean =>
  GREXEL_PLATFORM_REGIONS.has(resolveEffectiveRegion(registryCountryRegion));

/** Whether the statement goes to TGE (Poland), whose redemption form needs the end user's legal form and takes a VAT number. */
export const requiresTgeOptions = (
  registryCountryRegion: string | null | undefined
): boolean => resolveEffectiveRegion(registryCountryRegion) === "PL";

/**
 * Default cancellation purpose templates based on beneficiary type
 */
export const DEFAULT_CANCELLATION_PURPOSE = {
  CONSUMER: (beneficiaryName: string) =>
    `Proof of consumption for ${beneficiaryName}`,
  ELECTRICITY_SELLER: (beneficiaryName: string) =>
    `${beneficiaryName} electricity supply`,
};
